import { QueryClientProvider } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter, useLocation } from 'react-router-dom';

import LocaleToggle from '@/components/LocaleToggle';
import { AuthProvider } from '@/contexts/AuthContext';
import { DarkModeProvider } from '@/contexts/DarkModeContext';
import { TenantProvider } from '@/contexts/TenantContext';
import { queryClient } from '@/queryClient';
import AppRoutes from '@/routes/AppRoutes';
import tenantStore from '@/store';

const AUTH_TOKEN_KEYS = ['auth_token', 'auth_v2_accessToken', 'accessToken', 'authToken', 'token'];

const readAccessToken = (): string => {
  if (typeof window === 'undefined') return '';
  for (const key of AUTH_TOKEN_KEYS) {
    const value = String(window.localStorage.getItem(key) || '').trim();
    if (value && value.split('.').length >= 3) {
      return value;
    }
  }
  return '';
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = String(token || '').split('.')[1] || '';
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
};

const isAuthenticated = (): boolean => {
  const token = readAccessToken();
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp || 0);
  if (exp > 0 && Date.now() >= exp * 1000) return false;
  return true;
};

const getCurrentHost = (): string => {
  if (typeof window === 'undefined') return 'app.bassitai.com';
  return String(window.location.host || '').split(':')[0].toLowerCase();
};

const isLocalHost = (host: string): boolean =>
  host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');

const getBaseDomain = (host: string): string => {
  if (!host) return 'bassitai.com';
  if (isLocalHost(host)) return 'localhost';
  const parts = host.split('.').filter(Boolean);
  if (parts.length < 2) return host;
  return parts.slice(-2).join('.');
};

const buildLoginUrl = (): string => {
  if (typeof window === 'undefined') return 'https://login.bassitai.com/login';
  const host = getCurrentHost();
  const base = getBaseDomain(host);
  const protocol = isLocalHost(host) ? 'http' : 'https';
  const loginHost = base === 'localhost' ? 'login.localhost' : `login.${base}`;
  const returnTo = String(window.location.host || host);
  return `${protocol}://${loginHost}/login?returnTo=${encodeURIComponent(returnTo)}`;
};

function SilentUnauthenticatedGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isAuthContinueRoute = location.pathname === '/auth/continue';
  const authenticated = isAuthenticated();

  useEffect(() => {
    if (isAuthContinueRoute || authenticated) return;
    window.location.replace(buildLoginUrl());
  }, [authenticated, isAuthContinueRoute]);

  if (!isAuthContinueRoute && !authenticated) {
    return null;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ReduxProvider store={tenantStore}>
          <AuthProvider>
            <TenantProvider>
              <DarkModeProvider>
                <SilentUnauthenticatedGate>
                  <div style={{ minHeight: '100vh', background: 'var(--color-bg)', color: 'var(--color-text)' }}>
                    <LocaleToggle />
                    <AppRoutes />
                  </div>
                </SilentUnauthenticatedGate>
              </DarkModeProvider>
            </TenantProvider>
          </AuthProvider>
        </ReduxProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
